import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { AppButton, AppCard, WhatsAppButton } from '@/components/atoms';
import { type ClientSector } from '@/content/clients';

import { fleetCtaLabel, FLEET_FORM_ID, sectorCtaCopy, sectorWhatsAppMessage } from '../content';

type Props = {
  /** The roster's current filter; `null` is every sector. */
  sector: ClientSector | null;
  /** How many organisations the reader is looking at right now. */
  shown: number;
  /** Carries the sector into the form before the anchor scrolls to it. */
  onRequest: () => void;
};

/**
 * The ask, made at the foot of the roster rather than only at the foot of the
 * page.
 *
 * ## Why it is here and not only in the closing band
 *
 * This page used to end in the site's standard quotation band, three bands
 * below the roster, with the company story and the delivery archive in
 * between. Everything above that band was proof and none of it was an offer,
 * which is the usual failure of a client page: it answers "are you real" and
 * never asks the question that follows. The strongest moment on the page is
 * the instant a reader finishes reading their own sector's group — so the
 * offer is put there, inside the same band, immediately under the last card.
 *
 * ## Why it reads the filter
 *
 * A filtered roster is a visitor who has just declared their sector. The copy
 * answers with the count they are looking at and the offer in the same breath
 * (see `sectorCtaCopy`), and the WhatsApp message it opens names that sector,
 * so the conversation starts one question further along. Unfiltered, it falls
 * back to the roster's total — still a countable claim, just a broader one.
 *
 * ## Why the primary control is an anchor
 *
 * `href="#fleet-quote"` works in the prerendered HTML, survives a middle-click
 * and gives the browser the scrolling to do. The `onClick` beside it only
 * fills the form's sector in; if it never ran, the anchor would still land the
 * reader on a working form. A button calling `scrollIntoView` would have made
 * the navigation depend on the script.
 */
export function SectorMatchCta({ sector, shown, onRequest }: Props) {
  const copy = sectorCtaCopy(sector, shown);

  return (
    <AppCard sx={{ p: { xs: 6, md: 8 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 6, md: 8 }}
        sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}
      >
        <Stack spacing={2} sx={{ maxWidth: '58ch' }}>
          <Typography variant="overline" sx={{ color: 'primary.main' }}>
            {copy.eyebrow}
          </Typography>
          {/* An h3 under the band's h2: it is a block of the roster section,
              not a section of its own, and the sector headings beside it are
              h3s too. */}
          <Typography component="h3" variant="h4">
            {copy.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {copy.text}
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row', md: 'column' }}
          spacing={3}
          sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' } }}
        >
          <AppButton href={`#${FLEET_FORM_ID}`} onClick={onRequest} size="large" sx={{ fontSize: { xs: 15, md: 18 } }}>
            {fleetCtaLabel}
          </AppButton>
          {/* The second answer to the same question, for the reader who would
              rather type than fill in a form. One filled button and one
              outline: the form is the page's primary path. */}
          <WhatsAppButton message={sectorWhatsAppMessage(sector)} emphasis="outline" size="large" sx={{ fontSize: { xs: 15, md: 18 } }} />
        </Stack>
      </Stack>
    </AppCard>
  );
}
